import type React from "react";
import { Box, Paper, Typography } from "@mui/material";
import OrderStatusChip from "./OrderStatusChip";
import StatusProgressBar from "./StatusProgressBar";

interface DetailPageHeaderProps {
  title: string;
  subtitle: React.ReactNode;
  statusLabel?: string;
  status?: string;
  statusTestId?: string;
  /** Optional note shown inline next to the status chip (e.g. revision note). */
  statusNote?: React.ReactNode;
  metaLine?: React.ReactNode;
  /** Numeric progress bar (report pages). Ignored when `progressSlot` is set. */
  progress?: number;
  /** Custom progress UI such as a named workflow stepper. */
  progressSlot?: React.ReactNode;
  actions?: React.ReactNode;
  compact?: boolean;
  "data-testid"?: string;
}

const separator = (
  <Box component="span" sx={{ color: "text.disabled" }}>
    ·
  </Box>
);

const DetailPageHeader: React.FC<DetailPageHeaderProps> = ({
  title,
  subtitle,
  statusLabel,
  status,
  statusTestId,
  statusNote,
  metaLine,
  progress,
  progressSlot,
  actions,
  compact = false,
  "data-testid": testId,
}) => {
  const showStatus = Boolean(statusLabel && status);
  const showProgress = Boolean(progressSlot || progress !== undefined);

  if (compact) {
    return (
      <Paper
        elevation={0}
        data-testid={testId}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
          color: "text.primary",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            rowGap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              rowGap: 0.75,
              flexWrap: "wrap",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              component="h1"
              sx={{ fontWeight: 700, letterSpacing: "-0.01em", flexShrink: 0 }}
            >
              {title}
            </Typography>
            {separator}
            <Typography variant="body2" color="text.secondary" component="div" sx={{ minWidth: 0 }}>
              {subtitle}
            </Typography>
            {showStatus ? (
              <>
                {separator}
                <OrderStatusChip status={status as string} data-testid={statusTestId} />
                {statusNote}
              </>
            ) : null}
            {metaLine ? (
              <>
                {separator}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="div"
                  sx={{ minWidth: 0 }}
                >
                  {metaLine}
                </Typography>
              </>
            ) : null}
          </Box>
          {actions ? (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
                ml: "auto",
                justifyContent: "flex-end",
              }}
            >
              {actions}
            </Box>
          ) : null}
        </Box>

        {showProgress ? (
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
            {progressSlot ??
              (progress !== undefined ? (
                <StatusProgressBar progress={progress} status={status} />
              ) : null)}
          </Box>
        ) : null}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      data-testid={testId}
      sx={{
        mb: 2.5,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        color: "text.primary",
        border: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            {title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.25 }} component="div">
            {subtitle}
          </Typography>

          {showStatus && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary">
                {statusLabel}:
              </Typography>
              <OrderStatusChip status={status as string} data-testid={statusTestId} />
              {statusNote}
            </Box>
          )}

          {metaLine && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }} component="div">
              {metaLine}
            </Typography>
          )}
        </Box>

        {actions && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "flex-end",
            }}
          >
            {actions}
          </Box>
        )}
      </Box>

      {showProgress && (
        <Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 } }}>
          {progressSlot ??
            (progress !== undefined ? (
              <StatusProgressBar progress={progress} status={status} />
            ) : null)}
        </Box>
      )}
    </Paper>
  );
};

export default DetailPageHeader;
